import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuthStore, type Entity } from "@/stores/auth-store";
import { clearToken } from "@/lib/auth-token";
import { cn } from "@/lib/cn";
import { Bell, Calendar, Mail, Search, Settings, Plus, ChevronDown, LogOut, UserCog, Building2 } from "lucide-react";

interface SearchResult {
  type: "account" | "contact" | "deal" | "lead" | "candidate";
  id: string;
  label: string;
  sub?: string;
}

const QUICK_CREATE = [
  { to: "/leads/new", label: "Lead" },
  { to: "/contacts/new", label: "Contact" },
  { to: "/accounts/new", label: "Account" },
  { to: "/deals/new", label: "Deal" },
  { to: "/tasks/new", label: "Task" },
  { to: "/meetings/new", label: "Meeting" },
  { to: "/job-orders/new", label: "Job Order" },
  { to: "/candidates/new", label: "Candidate" },
  { to: "/gov-opportunities/new", label: "Gov Opportunity" },
  { to: "/projects/new", label: "Project" },
  { to: "/products/new", label: "Product" },
  { to: "/solutions/new", label: "Solution" },
  { to: "/competitors/new", label: "Competitor" },
];

const ENTITY_OPTIONS: { value: Entity | "ALL"; label: string }[] = [
  { value: "ALL",      label: "All Entities" },
  { value: "US",       label: "CBC US" },
  { value: "INDIA",    label: "CBC India" },
  { value: "BOTH",     label: "CBC Shared" },
  { value: "GLOCALIZE", label: "Glocalize" },
];

export function TopBar() {
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const viewEntity = useAuthStore((s) => s.viewEntity);
  const setViewEntity = useAuthStore((s) => s.setViewEntity);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canSwitchEntity = me?.role === "SUPER_ADMIN" || me?.role === "ADMIN";

  // Invalidate all cached data when entity context changes so lists refetch immediately.
  useEffect(() => {
    qc.invalidateQueries();
  }, [viewEntity]);
  const [entityOpen, setEntityOpen] = useState(false);
  const entityRef = useRef<HTMLDivElement>(null);

  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);

  const [quickOpen, setQuickOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const quickRef = useRef<HTMLDivElement>(null);
  const userRef  = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (quickRef.current && !quickRef.current.contains(e.target as Node)) setQuickOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setResults(null);
      if (entityRef.current && !entityRef.current.contains(e.target as Node)) setEntityOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced global search across accounts, contacts, deals, leads, candidates.
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const [acc, con, deal, lead, cand] = await Promise.all([
          api.get("/accounts",   { params: { q, pageSize: 5 } }).then((r) => r.data.data as any[]).catch(() => []),
          api.get("/contacts",   { params: { q, pageSize: 5 } }).then((r) => r.data.data as any[]).catch(() => []),
          api.get("/deals",      { params: { q, pageSize: 5 } }).then((r) => r.data.data as any[]).catch(() => []),
          api.get("/leads",      { params: { q, pageSize: 5 } }).then((r) => r.data.data as any[]).catch(() => []),
          api.get("/candidates", { params: { q, pageSize: 5 } }).then((r) => r.data.data as any[]).catch(() => []),
        ]);
        const hits: SearchResult[] = [
          ...acc.map((a) => ({ type: "account"   as const, id: a.id, label: a.name, sub: a.industry ?? a.type })),
          ...con.map((c) => ({ type: "contact"   as const, id: c.id, label: c.name, sub: c.email ?? c.title })),
          ...deal.map((d) => ({ type: "deal"     as const, id: d.id, label: d.name, sub: d.stage })),
          ...lead.map((l) => ({ type: "lead"     as const, id: l.id, label: l.name, sub: l.company })),
          ...cand.map((c) => ({ type: "candidate" as const, id: c.id, label: c.name, sub: c.location })),
        ];
        setResults(hits);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  function goToResult(r: SearchResult) {
    setQ(""); setResults(null);
    const path =
      r.type === "account"   ? `/accounts/${r.id}` :
      r.type === "contact"   ? `/contacts/${r.id}` :
      r.type === "deal"      ? `/deals`           :
      r.type === "lead"      ? `/leads`           :
      r.type === "candidate" ? `/candidates`      : "/";
    navigate(path);
  }

  function logout() { clearToken(); setMe(null); navigate("/login"); }

  return (
    <header className="flex h-14 items-center gap-3 border-b bg-card px-4">
      {/* Global search */}
      <div ref={searchRef} className="relative flex-1 max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search accounts, contacts, deals, leads, candidates…"
          className="h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        {results && (
          <div className="absolute left-0 right-0 top-full mt-1 max-h-96 overflow-auto rounded-md border bg-popover shadow-lg">
            {searching && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
            {!searching && results.length === 0 && <div className="p-3 text-xs text-muted-foreground">No results.</div>}
            {results.map((r) => (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => goToResult(r)}
                className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm hover:bg-muted last:border-0"
              >
                <div>
                  <div className="font-medium">{r.label}</div>
                  {r.sub && <div className="text-xs text-muted-foreground">{r.sub}</div>}
                </div>
                <span className="rounded bg-secondary px-1.5 py-0.5 text-xs uppercase text-muted-foreground">{r.type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Entity switcher — SUPER_ADMIN / ADMIN only */}
      {canSwitchEntity && (
        <div ref={entityRef} className="relative ml-2">
          <button
            onClick={() => setEntityOpen((o) => !o)}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
              viewEntity === "ALL"
                ? "border-border text-muted-foreground hover:bg-muted"
                : "border-primary/40 bg-primary/10 text-primary",
            )}
          >
            <Building2 className="h-3.5 w-3.5" />
            {ENTITY_OPTIONS.find((o) => o.value === viewEntity)?.label ?? "All Entities"}
            <ChevronDown className="h-3 w-3" />
          </button>
          {entityOpen && (
            <div className="absolute left-0 top-full z-50 mt-1 w-44 rounded-md border bg-popover py-1 text-sm shadow-lg">
              <div className="px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground">View as entity</div>
              {ENTITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setViewEntity(opt.value); setEntityOpen(false); }}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-muted",
                    viewEntity === opt.value && "font-semibold text-primary",
                  )}
                >
                  {opt.label}
                  {viewEntity === opt.value && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* Quick create */}
        <div ref={quickRef} className="relative">
          <button
            onClick={() => setQuickOpen((o) => !o)}
            aria-label="Quick create"
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Plus className="h-5 w-5" />
          </button>
          {quickOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 rounded-md border bg-popover py-1 text-sm shadow-lg">
              <div className="px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground">Quick create</div>
              {QUICK_CREATE.map((q) => (
                <Link key={q.to} to={q.to} onClick={() => setQuickOpen(false)} className="block px-3 py-1.5 hover:bg-muted">
                  {q.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Placeholder icons — wire to real pages later */}
        <button aria-label="Calendar" title="Calendar" className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
          <Calendar className="h-5 w-5" />
        </button>
        <button aria-label="Inbox" title="Inbox" className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
          <Mail className="h-5 w-5" />
        </button>
        <button aria-label="Notifications" title="Notifications" className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
          <Bell className="h-5 w-5" />
        </button>
        <Link to="/admin" title="Admin & settings" className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
          <Settings className="h-5 w-5" />
        </Link>

        {/* User menu */}
        <div ref={userRef} className="relative ml-1">
          <button
            onClick={() => setUserOpen((o) => !o)}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
              {(me?.name ?? "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
          {userOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 rounded-md border bg-popover py-1 text-sm shadow-lg">
              <div className="border-b px-3 py-2">
                <div className="font-medium">{me?.name}</div>
                <div className="text-xs text-muted-foreground">{me?.email}</div>
                <div className="text-xs text-muted-foreground">
                  {me?.role.replace(/_/g, " ")} · {me?.entity}
                  {canSwitchEntity && viewEntity !== "ALL" && (
                    <span className="ml-1 rounded bg-primary/15 px-1 text-primary">viewing {viewEntity}</span>
                  )}
                </div>
              </div>
              <Link to="/admin" onClick={() => setUserOpen(false)} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted">
                <UserCog className="h-4 w-4" /> Admin
              </Link>
              <button onClick={logout} className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left text-destructive hover:bg-muted")}>
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
